import { RefreshCcw } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Pulse } from '@unistocks/components/animations/pulse';
import { Image } from '@unistocks/components/basic/image';
import { ErrorMessage } from '@unistocks/components/error-message/error-message';
import { Form } from '@unistocks/components/form/form';
import { Center } from '@unistocks/components/layout/center';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { Page } from '@unistocks/components/page/page';
import { H2, Paragraph } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { ExternalPortfolios } from '@unistocks/features/external-accounts/components/external-portfolios';
import { AddAccountHeaderRight } from '@unistocks/features/external-accounts/header/header-right';
import { ImportHeaderTitle } from '@unistocks/features/external-accounts/header/header-title';
import { InstrumentValidationError } from '@unistocks/features/external-accounts/models/instrument-validation-error';
import {
  findMatchingSection,
  getInstrumentValidationError
} from '@unistocks/features/external-accounts/utils';
import { selectSelectedPortfolioId } from '@unistocks/features/portfolios/store/portfolio.slice';
import { formatDate, t } from '@unistocks/localization';
import {
  ExternalAccountDto,
  ExternalAccountUpdateDto,
  ExternalPortfolioDto,
  InstrumentDto,
  ListResultDtoOfLookupDtoOfGuid
} from '@unistocks/proxy/axios-client';
import {
  externalAccountsGetAccountQueryKey,
  externalAccountsGetPortfoliosQueryKey,
  useExternalAccountsGetAccountQuery,
  useExternalAccountsGetPortfoliosQuery,
  useExternalAccountsGetProvidersQuery,
  useExternalAccountsImportExternalPortfoliosMutation,
  useExternalAccountsUpdateAccountMutation
} from '@unistocks/proxy/axios-client/ExternalAccountsQuery';
import {
  usePortfoliosGetListQuery,
  usePortfoliosGetQuery,
  usePortfoliosGetSyncSettingsQuery
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import { UnistocksExternalFeatures } from '@unistocks/shared/features';
import { useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { useFeatureEnabled } from '@unistocks/shared/hooks/use-feature';
import { isHandledError } from '@unistocks/shared/utils/error';
import { isArray, isBoolean, moveItemInArray, noop } from '@unistocks/shared/utils/util';
import { SelectOption } from '@unistocks/types';
import { ExternalErrorCode } from '@unistocks/types/error/error-code';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Duration } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, CheckedState } from 'tamagui';
import { z } from 'zod';

const schema = z.object({
  syncTransactions: z.boolean().nullish()
});

const mapPortfolios = ({ items }: ListResultDtoOfLookupDtoOfGuid) =>
  items.map(
    ({ id, displayName }) =>
      ({ key: displayName, value: id, icon: <UnistocksCircle /> }) as SelectOption
  );

type Params = {
  provider: string;
  identifier?: string;
  accountIdentifier?: string;
};

export const Import: React.FC = () => {
  const { provider, accountIdentifier } = useLocalSearchParams<Params>();
  const router = useRouter();
  const queryClient = useQueryClient();
  const hasTransactionSync = useFeatureEnabled(UnistocksExternalFeatures.TransactionSync);
  const activePortfolio = useAppSelector(selectSelectedPortfolioId);
  const [targetPortfolio, setTargetPortfolio] = useState<string | null>();
  const [selectedForImport, setSelectedFormImport] = useState<Record<string, CheckedState>>({});
  const [updatedExternalPortfolios, setUpdatedExternalPortfolios] = useState<
    ExternalPortfolioDto[] | null
  >(null);

  const { data: providers, isSuccess: isSuccessProviders } = useExternalAccountsGetProvidersQuery({
    countryCode: 'SE'
  });

  const selectedProvider = providers?.items.find(p => p.name === provider);

  const {
    data: externalAccount,
    isLoading: isLoadingExternalAccount,
    isSuccess: isSuccessExternalAccount,
    isError: isErrorExternalAccount,
    refetch: refetchExternalAccount
  } = useExternalAccountsGetAccountQuery(
    { provider, accountIdentifier },
    {
      enabled: !!provider
    }
  );

  const initialValues: ExternalAccountUpdateDto = {
    syncTransactions: externalAccount?.syncTransactions ?? true
  };

  const { mutate: updateExternalAccount, isPending: isExternalAccoutUpdatePending } =
    useExternalAccountsUpdateAccountMutation(provider, accountIdentifier, {
      onMutate: (newData: ExternalAccountUpdateDto) => {
        queryClient.setQueryData(
          externalAccountsGetAccountQueryKey({
            provider: provider!,
            accountIdentifier
          }),
          (oldData: ExternalAccountDto) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              syncTransactions: newData.syncTransactions
            } as ExternalAccountDto;
          }
        );
      },
      onSuccess: () => {
        toast.success(t('AbpAccount::PersonalSettingsSaved'));
      },
      onError: () => {
        queryClient.invalidateQueries({
          queryKey: externalAccountsGetAccountQueryKey({
            provider: provider!,
            accountIdentifier: accountIdentifier
          })
        });
        toast.error(t('::Settings:Update:Error'));
      }
    });

  const {
    data: externalPortfolios,
    isPending: isLoadingExternalPortfoliosQuery,
    isSuccess: isSuccessExternalPortfolios,
    isError: isErrorExternalPortfolios,
    error: externalPortfoliosError,
    isRefetching: isRefetchingExternalPortfolios,
    refetch: refetchExternalPortfolios
  } = useExternalAccountsGetPortfoliosQuery(provider, accountIdentifier, {
    enabled: !!provider,
    staleTime: Duration.fromObject({ hour: 12 }).toMillis()
  });
  const isWaitingForDataError =
    isErrorExternalPortfolios &&
    isHandledError(externalPortfoliosError) &&
    externalPortfoliosError.error.code === ExternalErrorCode.NoProviderData;
  const isLoadingExternalPortfolios = isLoadingExternalPortfoliosQuery || isWaitingForDataError;

  useEffect(() => {
    let interval: NodeJS.Timeout;

    // Data is being fetched in the background
    if (isWaitingForDataError) {
      interval = setInterval(() => {
        refetchExternalAccount();
        refetchExternalPortfolios();
      }, 2_000);
    }

    return () => (interval ? clearInterval(interval) : undefined);
  }, [isWaitingForDataError, refetchExternalAccount, refetchExternalPortfolios]);

  const { data: portfolios, isSuccess: isSuccessPortfolios } = usePortfoliosGetListQuery({
    structuralSharing: false,
    select: mapPortfolios
  });

  // The portfolio to import into: the user's explicit pick, else the active
  // portfolio, else the first available one — derived rather than synced into
  // state from an effect.
  const resolvedTargetPortfolio = targetPortfolio ?? activePortfolio ?? portfolios?.[0]?.value;

  const {
    data: portfolio,
    isSuccess: isSuccessPortfolio,
    isLoading: isLoadingPortfolio
  } = usePortfoliosGetQuery(
    { id: resolvedTargetPortfolio! },
    {
      enabled: !!resolvedTargetPortfolio
    }
  );

  /*
    Load previously saved sync settings for the portfolio (if any) and prefill import settings.
    This is only needed when importing into an existing portfolio (not when creating a new one).
    The sync settings are available in the portfolio, but it does not include hidden assets!
  */
  const { data: syncSettings, isSuccess: isSuccessSyncSettings } =
    usePortfoliosGetSyncSettingsQuery(resolvedTargetPortfolio!, provider, accountIdentifier);

  const { mutate: saveImports, isPending } = useExternalAccountsImportExternalPortfoliosMutation({
    onSuccess: () => {
      toast.info(t('::Import:Notification:InProgress:Title'), {
        description: t('::Import:Notification:InProgress:Message', {
          0: externalAccount!.provider.displayName
        }),
        duration: 8_000
      });

      router.navigate({
        pathname: '/portfolios/[id]',
        params: { id: resolvedTargetPortfolio! }
      });
    }
  });

  const isImportDataReady =
    isSuccessPortfolio && isSuccessExternalPortfolios && isSuccessSyncSettings;

  // Apply the synced-import defaults to a COPY of the fetched portfolios. Doing
  // this as a pure derivation keeps the react-query cache immutable (previously
  // an effect mutated `externalPortfolios.items` in place) and lets `validation`
  // derive during render instead of being synced from an effect. Once the user
  // edits settings we keep their copy as-is.
  const displayPortfolios = useMemo<ExternalPortfolioDto[]>(() => {
    if (!externalPortfolios?.items) {
      return updatedExternalPortfolios ?? [];
    }

    if (updatedExternalPortfolios) {
      return updatedExternalPortfolios;
    }

    if (!isImportDataReady || !portfolio) {
      return externalPortfolios.items;
    }

    const assets = portfolio.sections.flatMap(section => section.assets);
    const syncedInstruments = syncSettings?.items ?? [];

    return externalPortfolios.items.map(externalPortfolio => ({
      ...externalPortfolio,
      instruments: externalPortfolio.instruments.map((instrument: InstrumentDto) => {
        const existingAsset = assets.find(asset =>
          asset.syncedAssets.find(
            syncedAsset =>
              syncedAsset.id === instrument.id &&
              syncedAsset.portfolioId === instrument.portfolioId
          )
        );
        const previouslySyncedInstrument = syncedInstruments.find(
          syncedAsset =>
            syncedAsset.id === instrument.id &&
            syncedAsset.portfolioId === instrument.portfolioId
        );
        const sectionId = existingAsset?.sectionId ?? findMatchingSection(instrument, portfolio);

        if (previouslySyncedInstrument) {
          const searchResult = isArray(instrument.searchResult)
            ? [...instrument.searchResult]
            : instrument.searchResult;

          if (isArray(searchResult) && instrument.isin) {
            const index = searchResult.findIndex(
              x =>
                x.isin === previouslySyncedInstrument.isin &&
                x.identifier === existingAsset?.identifier
            );

            // Move the matching search result to the top of the list (will be the selected search result)
            if (index > -1) {
              moveItemInArray(searchResult, index, 0);
            }
          }

          return {
            ...instrument,
            importSettings: {
              ...previouslySyncedInstrument.importSettings,
              sectionId
            },
            searchResult
          };
        }

        return {
          ...instrument,
          importSettings: {
            ...instrument.importSettings,
            sectionId,
            isManualAsset: !!instrument.isin && instrument.searchResult.length === 0
          }
        };
      })
    }));
  }, [
    externalPortfolios,
    updatedExternalPortfolios,
    isImportDataReady,
    portfolio,
    syncSettings
  ]);

  const validation = useMemo<InstrumentValidationError | null>(() => {
    if (!portfolio) {
      return null;
    }

    let result: InstrumentValidationError | null = null;
    const instrumentsToValidate = displayPortfolios
      .filter(x => selectedForImport[x.id])
      .flatMap(x => x.instruments);

    for (const instrument of instrumentsToValidate) {
      const instrumentValidation = getInstrumentValidationError(instrument, portfolio);
      if (instrumentValidation) result = instrumentValidation;

      if (result === 'Error') break;
    }

    return result;
  }, [displayPortfolios, portfolio, selectedForImport]);

  const onConfirm = () => {
    if (provider && resolvedTargetPortfolio && displayPortfolios.length) {
      const portfoliosToImport = displayPortfolios.filter(x => selectedForImport[x.id]);
      saveImports({
        portfolioId: resolvedTargetPortfolio,
        providerName: provider,
        accountIdentifier: accountIdentifier || null,
        externalPortfolios: portfoliosToImport
      });
    }
  };

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({
        queryKey: externalAccountsGetPortfoliosQueryKey({
          provider: provider!,
          accountIdentifier: accountIdentifier
        })
      });
    };
  }, [accountIdentifier, provider, queryClient]);

  const onTransactionSyncChange = (checked: CheckedState) => {
    if (isBoolean(checked)) {
      updateExternalAccount({ syncTransactions: checked });
    }
  };

  const onInstrumentSettingsChange = (updatedInstrument: InstrumentDto) => {
    if (!displayPortfolios.length) return;

    setUpdatedExternalPortfolios(prevPortfolios => {
      const currentPortfolios = prevPortfolios || displayPortfolios;

      return currentPortfolios.map(portfolio => ({
        ...portfolio,
        instruments: portfolio.instruments.map((instrument: InstrumentDto) =>
          instrument.id === updatedInstrument.id &&
          instrument.portfolioId === updatedInstrument.portfolioId
            ? updatedInstrument
            : instrument
        )
      }));
    });
  };

  const isLoading = isLoadingExternalAccount || isLoadingExternalPortfolios;
  const isSuccess = isSuccessExternalAccount && isSuccessExternalPortfolios;
  const isError =
    (isErrorExternalAccount || isErrorExternalPortfolios) && !isLoadingExternalPortfolios;

  return (
    <Page size="medium">
      <Page.ScrollView
        style={{ overflow: 'visible' }}
        onRefresh={refetchExternalPortfolios}
        refreshing={isRefetchingExternalPortfolios}
        headerLogo={false}
        headerTitle={t('::Menu:Import')}
        headerSubtitle={
          isSuccessPortfolios ? (
            <ImportHeaderTitle
              value={resolvedTargetPortfolio}
              onChange={setTargetPortfolio}
            >
              {isSuccessPortfolio ? portfolio.name : isLoadingPortfolio ? <Spinner /> : ''}
            </ImportHeaderTitle>
          ) : undefined
        }
        headerIcon={
          <AddAccountHeaderRight isPending={isPending} validation={validation} onSave={onConfirm} />
        }
        breadcrumbs={[
          {
            key: 'external-accounts',
            rootPathName: 'external-accounts',
            name: t('::ExternalAccounts'),
            href: '/(tabs)/(menu)/external-accounts',
            Icon: props => <RefreshCcw {...props} />
          }
        ]}
      >
        <AnimatePresence mode="wait" initial={false}>
          {isSuccess ? (
            <Page.Content key={'content'} gap={'$3'}>
              <Row gap={'$3'} alignItems={'center'}>
                <Image rounded size={40} source={externalAccount.provider.imageUri} />
                <Column>
                  <H2 color={'$headingFont'} size={'$8'} userSelect="none">
                    {externalAccount.provider.displayName}
                  </H2>
                  {externalAccount.updated ? (
                    <Paragraph opacity={0.6}>
                      {`${t('::LastSynchronization')}: ${formatDate(externalAccount.updated, {
                        dateStyle: 'short'
                      })}`}
                    </Paragraph>
                  ) : null}
                </Column>
              </Row>

              {hasTransactionSync ? (
                <Form
                  schema={schema}
                  initialValues={initialValues}
                  isLoading={isExternalAccoutUpdatePending}
                  onSubmit={noop}
                  customTrigger={null}
                >
                  <Form.Checkbox
                    name="syncTransactions"
                    label={t('::Import:SyncTransactions')}
                    onCheckedChange={onTransactionSyncChange}
                  />
                </Form>
              ) : null}

              <ExternalPortfolios
                externalAccount={externalAccount!}
                externalPortfolios={displayPortfolios}
                targetPortfolio={portfolio}
                selectedForImport={selectedForImport}
                setSelectedFormImport={setSelectedFormImport}
                onInstrumentSettingsChange={onInstrumentSettingsChange}
              />
            </Page.Content>
          ) : null}

          {isLoading ? (
            isSuccessProviders && selectedProvider ? (
              <Center key={'provider'} exitStyle={{ o: 0, y: -20 }}>
                <Pulse scale={1.4}>
                  <Image rounded size={'$10'} source={selectedProvider.imageUri} />
                </Pulse>
                <Paragraph size={'$7'} mt={'$5'}>
                  {t('::Import:Loading{0}', { 0: selectedProvider?.displayName })}
                </Paragraph>
              </Center>
            ) : (
              <Loader key={'loader'}>{t('::Import:Loading')}</Loader>
            )
          ) : null}

          {isError ? (
            <ErrorMessage
              key={'error-message'}
              minH={'$20'}
              message={t('::Import:Error:GetInvestments', {
                0: externalAccount?.provider.displayName ?? provider
              })}
              onRetry={refetchExternalPortfolios}
            />
          ) : null}
        </AnimatePresence>
      </Page.ScrollView>
    </Page>
  );
};
